import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileClockIcon, LanguagesIcon } from 'lucide-react';
import { SmartLink } from '../components/SmartLink';
import { LegalHeader, type LegalLanguage } from '../components/legal/LegalHeader';
import { LegalNav } from '../components/legal/LegalNav';
import { SummaryBox } from '../components/legal/SummaryBox';
import { LegalToc, MobileToc } from '../components/legal/LegalToc';
import { LegalSectionView } from '../components/legal/LegalSectionView';
import { LegalContactCard } from '../components/legal/LegalContactCard';
import { PlaceholderNote } from '../components/company/PlaceholderNote';
import { useScrollSpy } from '../hooks/useScrollSpy';
import { legalDocs } from '../data/legal';
import { legalContact, legalNav } from '../data/legal/navigation';
import { NotFound } from './NotFound';
export function LegalPage() {
  const {
    slug = ''
  } = useParams();
  const [language, setLanguage] = useState<LegalLanguage>('English');
  const navItem = legalNav.find(item => item.slug === slug);
  const doc = legalDocs[slug];
  const activeId = useScrollSpy(doc ? doc.sections.map(s => s.id) : []);
  if (!navItem) return <NotFound data-id="element-2076" data-mp="18ei99gboa65p" />;
  return <main className="w-full bg-white" data-id="element-2077" data-mp="18fb71rsnllbp">
      <div className="hero-surface w-full" data-id="element-2078" data-mp="18e8gxm35nmwu">
        <div className="mx-auto max-w-shell px-6 pb-8 pt-12 lg:pt-16" data-id="element-2079" data-mp="1ddcs2i9n3upt">
          <LegalHeader title={doc?.title ?? navItem.label} lastUpdated={doc?.lastUpdated} version={doc?.version} language={language} onLanguageChange={setLanguage} data-id="element-2080" data-mp="1dffnxixh4i2g" />
          {language !== 'English' && <p role="status" className="mt-5 flex items-center gap-2 text-[14px] text-ink-muted" data-id="element-2081" data-mp="ppv4kyxerisn">
              <LanguagesIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-2082" data-mp="tpvhug8rtny4" />
              The {language} version is being prepared. Showing English for now.
            </p>}
          <div className="mt-8" data-id="element-2083" data-mp="knmbm2kjcgqj">
            <LegalNav items={legalNav} current={slug} data-id="element-2084" data-mp="kqrwf5m4swgg" />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-shell px-6 pb-24 pt-10 lg:pt-14" data-id="element-2085" data-mp="2mfxty2s16ajv">
        {doc ? <div className="grid grid-cols-1 gap-8 lg:grid-cols-[240px_minmax(0,720px)] lg:gap-16 xl:gap-24" data-id="element-2086" data-mp="2hla1jstwow00">
            <LegalToc sections={doc.sections} activeId={activeId} data-id="element-2087" data-mp="2djf1ssrmiqoc" />
            <div className="min-w-0" data-id="element-2088" data-mp="28o9j02tips4h">
              <div className="space-y-5" data-id="element-2089" data-mp="35cz03qxbmgpe">
                <MobileToc sections={doc.sections} activeId={activeId} data-id="element-2090" data-mp="35bxptoo4py8q" />
                <PlaceholderNote data-id="element-2091" data-mp="31bna3kajbtgo">
                  Placeholder legal text. The content below is drafting notes for review and is not final wording.
                </PlaceholderNote>
                <SummaryBox points={doc.summary} data-id="element-2092" data-mp="2rd6eub4kcsot" />
              </div>
              <article className="mt-12 space-y-14" data-id="element-2093" data-mp="2rcy1ki4j3ld3">
                {doc.sections.map((section, i) => <LegalSectionView key={section.id} section={section} number={i + 1} data-id="element-2094" data-mp="1kv1288kvzmao" data-mpi={i} />)}
              </article>
              <div className="mt-16" data-id="element-2095" data-mp="15ucduknpta8o">
                <LegalContactCard email={legalContact.email} address={legalContact.address} data-id="element-2096" data-mp="15vf3kp9ax6n0" />
              </div>
            </div>
          </div> : <div className="mx-auto max-w-[520px] rounded-[14px] border border-canvas-line px-6 py-12 text-center" data-id="element-2097" data-mp="1fsju11a6jo9m">
            <FileClockIcon className="mx-auto h-8 w-8 text-brand" strokeWidth={1.7} aria-hidden="true" data-id="element-2098" data-mp="1kji8dvblyca8" />
            <h2 className="mt-5 text-[20px] font-semibold tracking-[-0.02em] text-ink" data-id="element-2099" data-mp="1old9oztjgpo0">This document is being prepared</h2>
            <p className="mt-2 text-[15px] leading-[1.6] text-ink-muted" data-id="element-2100" data-mp="1tidllrd2kmn1">
              The {navItem.label.toLowerCase()} will be published here soon. For questions in the meantime, contact our team.
            </p>
            <SmartLink href="/contact" className="mt-6 inline-flex h-11 items-center rounded-[7px] bg-brand px-5 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-2101" data-mp="23h2zl91uhmy0">
              Contact us
            </SmartLink>
          </div>}
      </div>
    </main>;
}